import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  updateSection,
  setSection,
} from "../../../../../../features/assignment/assignmentSlice";

import { tagInfo, phraseInfo } from "../constants";

import { getContentHeight } from "../../../../../../utils/textUtils";
import { getStartingIndex } from "../../../../../../utils/resumeUtils";
import { compareGroupData, comparePhraseData } from "./handleSubmit";
import { 
  evaluateAdvancedSyntax
} from "../../../../../../utils/syntaxEvaluation";
import { 
  updateAdvancedPageWithCorrectness
} from "../../../../../../utils/syntaxDataHelpers";

import AdvancedGroupElement from "./AdvancedGroupElement";
import ArrowButton from "../../../widgets/ArrowButton";
import CorrectOverlay from "../../../widgets/CorrectOverlay";
import Toast from "../../../widgets/Toast";
import TagButtonList from "../TagButtonList";
import { refinePageList } from "./refinePageList";
import AdvancedTokenElement from "./AdvancedTokenElement";
import { togglePhraseData } from "./phraseData";
import { orderedTokenList } from "../../../../../../utils/answerUtils";

const AdvancedPage = ({ section, setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const [pageList, setPageList] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayType, setOverlayType] = useState('correct'); // 'correct' or 'incorrect'
  const [elementCorrectness, setElementCorrectness] = useState({});
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const lineHeight = Math.min(window.innerWidth, window.screen.width) <= 768 ? "42px" : "50px";
  const currentPage = pageList[currentIndex] || {
    type: "",
    data: [],
    tags: [],
  };

  useEffect(() => {
    if (pageList.length > 0) {
      return;
    }
    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    if (section.question) {
      refinePageList({ setPageList, section });
    }
  }, [section]);

  const handleClickToken = (element, elementIndex) => {
    if (currentPage.isSubmitted) {
      return;
    }
    const pageData = currentPage.data.slice();
    const newGroup = {
      type: "group",
      text: [
        {
          type: element.type,
          text: element.text,
          index: element.index,
          space_right: element.space_right,
        },
      ],
    };
    pageData.splice(elementIndex, 1, newGroup);

    let newPageList = [];

    if (selectedGroupIndex === -1) {
      setSelectedGroupIndex(elementIndex);
      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));
    } else {
      const selectedGroup = pageList[currentIndex].data[selectedGroupIndex];
      if (elementIndex > selectedGroupIndex && selectedGroup.text.length === 1) {
        newPageList = pageList.map((page, pageIndex) => {
          if (pageIndex === currentIndex) {
            const pageData = page.data.slice();
            // 사이에 이미 만들어 둔 묶음이 있으면 그 안의 낱말을 풀어서 담는다.
            // 덩어리째 담으면 낱말 하나가 빈칸이 되고 같은 낱말이 뒤에 또 나왔다.
            const newGroup = {
              ...selectedGroup,
              text: orderedTokenList([
                ...selectedGroup.text,
                ...pageData.slice(selectedGroupIndex + 1, elementIndex + 1),
              ]),
            };
            pageData.splice(
              selectedGroupIndex,
              elementIndex - selectedGroupIndex + 1,
              newGroup
            );
            return {
              ...page,
              data: pageData,
            };
          }
          return page;
        });
        setPageList(newPageList);
        dispatch(setSection({ ...section, answer: newPageList }));
      }
    }
  };

  const handleClickGroup = (element, elementIndex) => {
    if (currentPage.isSubmitted) {
      return;
    }

    const currentPageData = currentPage.data.slice();
    currentPageData.splice(elementIndex, 1, ...element.text);
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          data: currentPageData,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setSelectedGroupIndex(-1);
  };

  const handleClickTag = (tag) => {
    insertTagData({
      tag: tag,
    });
    setSelectedGroupIndex(-1);
  };

  const handlePrev = () => {
    if (selectedGroupIndex !== -1) {
      handleClickGroup(
        pageList[currentIndex].data[selectedGroupIndex],
        selectedGroupIndex
      );
    }
    setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      if (selectedGroupIndex !== -1) {
        handleClickGroup(
          pageList[currentIndex].data[selectedGroupIndex],
          selectedGroupIndex
        );
      }
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllSubmitted = true;
      pageList.forEach((page) => {
        if (page.isSubmitted === false) {
          isAllSubmitted = false;
          return;
        }
      });
      if (isAllSubmitted) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습을 완료하셨습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const insertTagData = ({ tag }) => {
    if (selectedGroupIndex === -1) {
      return;
    }

    const allTagList = Object.keys(tagInfo);

    const currentPage = pageList[currentIndex];
    const selectedGroup = currentPage.data[selectedGroupIndex];

    let newPageList = [];

    if (allTagList.includes(tag)) {
      const groupData = Object.assign({}, currentPage.groupData);
      const groupLabelIndex = Math.floor(selectedGroup.text.length / 2);

      for (let i = 0; i < selectedGroup.text.length; i++) {
        groupData[selectedGroup.text[i].index] = {
          groupType: tag,
          isGroupEnd: i === selectedGroup.text.length - 1,
          isGroupLabel: i === groupLabelIndex,
          space_right: selectedGroup.text[i].space_right,
        };
      }

      const pageData = currentPage.data.slice();
      pageData.splice(selectedGroupIndex, 1, ...selectedGroup.text);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
            groupData: groupData,
          };
        }
        return page;
      });
    } else {
      const startIndex = selectedGroup.text[0].index;
      const endIndex = selectedGroup.text[selectedGroup.text.length - 1].index;
      const phraseData = togglePhraseData({
        phraseData: currentPage.phraseData,
        phrase: phraseInfo[tag],
        startIndex,
        endIndex,
      });

      const pageData = currentPage.data.slice();
      pageData.splice(selectedGroupIndex, 1, ...selectedGroup.text);

      newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            data: pageData,
            phraseData: phraseData,
          };
        }
        return page;
      });
    }
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) {
      return;
    }

    const submitCount = currentPage.submitCount + 1;

    let evaluationResult;
    let newCurrentPage;

    // Check if max attempts (3) reached
    if (submitCount >= 3) {
      // Auto-reveal all correct answers after 3 attempts
      const allTagList = Object.keys(tagInfo);
      const allPhraseList = Object.keys(phraseInfo);

      const autoRevealedGroupData = {};
      const autoRevealedPhraseData = {};

      // Process answerData to reveal correct answers
      currentPage.answerData.forEach(answer => {
        if (allTagList.includes(answer.type)) {
          // Add to groupData
          const groupLabelIndex = Math.floor(answer.token_indices.length / 2);
          answer.token_indices.forEach((tokenIndex, idx) => {
            autoRevealedGroupData[tokenIndex] = {
              groupType: answer.type,
              isGroupEnd: idx === answer.token_indices.length - 1,
              isGroupLabel: idx === groupLabelIndex,
              space_right: currentPage.data[tokenIndex]?.space_right || true
            };
          });
        } else if (allPhraseList.includes(answer.type)) {
          // Add to phraseData
          const startIndex = answer.token_indices[0];
          const endIndex = answer.token_indices[answer.token_indices.length - 1];

          if (!autoRevealedPhraseData[startIndex]) {
            autoRevealedPhraseData[startIndex] = { start: [], end: [] };
          }
          autoRevealedPhraseData[startIndex].start.push({
            ...phraseInfo[answer.type],
            endIndex: endIndex
          });

          if (!autoRevealedPhraseData[endIndex]) {
            autoRevealedPhraseData[endIndex] = { start: [], end: [] };
          }
          autoRevealedPhraseData[endIndex].end.push({
            ...phraseInfo[answer.type],
            startIndex: startIndex
          });
        }
      });

      // Mark all answers as submitted
      const autoRevealedAnswers = currentPage.answerData.map(answer => ({
        ...answer,
        isSubmitted: true,
        isCorrect: true,
        studentAnswer: answer.type,
        autoRevealed: true
      }));

      newCurrentPage = {
        ...currentPage,
        groupData: autoRevealedGroupData,
        phraseData: autoRevealedPhraseData,
        answerData: autoRevealedAnswers,
        submitCount: submitCount,
        isSubmitted: true,
        autoRevealed: true
      };

      setOverlayType('incorrect'); // Show X overlay for max attempts
    } else {
      // Normal evaluation (attempts 1-2)
      evaluationResult = evaluateAdvancedSyntax(
        currentPage.groupData,
        currentPage.phraseData,
        currentPage.answerData,
        currentIndex
      );

      // Update page with correctness information
      newCurrentPage = updateAdvancedPageWithCorrectness(currentPage, evaluationResult);
      newCurrentPage.submitCount = submitCount;
      newCurrentPage.isSubmitted = evaluationResult.isAllCorrect;

      // Store correctness for overlay display
      setElementCorrectness(evaluationResult.correctness);
      setOverlayType(evaluationResult.isAllCorrect ? 'correct' : 'incorrect');
    }

    // Update page list
    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return newCurrentPage;
      }
      return prevPage;
    });

    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    // 미리보기와 검토에서는 서버에 쓰지 않는다. 교사가 눌러 본 답이 학생 답안을
    // 덮어썼고, 로그인 없이 여는 체험 과제에서는 섹션 번호가 숫자가 아니라
    // 500 이 났다(2026-09-01). 아래 마무리 저장은 이미 같은 조건으로 막혀 있다.
    if (!isPreview && !isReview) {
      dispatch(
        updateSection({ sectionId: section.id, data: { answer: newPageList } })
      );
    }

    // Show overlay feedback or toast
    if (submitCount < 3) {
      setOverlayVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);

        // Auto-advance if all correct
        if (newCurrentPage.isSubmitted) {
          setTimeout(() => {
            if (currentIndex < pageList.length - 1) {
              setCurrentIndex(currentIndex + 1);
            }
          }, 200);
        }
      }, 600);
    } else {
      // Max attempts reached - show X overlay and toast (user must manually navigate)
      setOverlayVisible(true);
      setToastMessage("제출 횟수를 초과하였습니다.\n정답을 확인하고, 다음 문제로 넘어가세요.");
      setToastVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
      setTimeout(() => {
        setToastVisible(false);
      }, 3000);
    }
  };

  const deleteData = () => {
    setPageList(
      pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          return {
            ...page,
            groupData: {},
            phraseData: {},
          };
        }
        return page;
      })
    );
  };

  return (
    <>
      <div
        className="syntax-frame"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "20px",
          justifyContent: "space-between",
          // [v17] height:100%는 부모가 높이를 안 주면 내용 높이로 접힌다 →
          // 화면 높이에서 상단바(45)+하단바(40)를 뺀 실측값으로 고정
          height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "calc(100vh - 155px)" : "100%",
          fontFamily: '"Noto Sans KR", sans-serif',
          backgroundColor: "#FFFFFF",
        }}
      >
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <ArrowButton
          direction="left"
          onClick={() => handlePrev()}
          visibleCondition={currentIndex > 0}
        />
        )}
        <div
          className="syntax-column"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center", // [v10] 짧으면 가운데, 길면 스크롤로 자연 확장
            width: Math.min(window.innerWidth, window.screen.width) <= 768 ? "100%" : "90%",
            height: "100%",
            flex: 1, // [v17] 부모 높이를 확실히 채워 justifyContent:center가 화면 기준이 되게
            maxWidth: "100%", // [v18] 컬럼이 화면보다 넓어지는 것 차단
            overflowX: "hidden", // [v18] 라벨 삐져나감이 폭을 늘리던 것 차단
            gap: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "20px",
            // [구문분석 모바일] 긴 문장 위아래 잘림 → 세로 스크롤 + 버튼 여유
            overflowY: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
            paddingBottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "16px" : 0, // [v15] 과한 하단 여백 제거 — 내용이 진짜 중앙에
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "13px" : "20px",
              marginBottom: "20px",
              textAlign: "center", // [v12]
              width: "100%",
            }}
          >
            <span className="syntax-instruction">아래 표시된 단어 (구, 절)의 문장 형식을 찾아 선택하시오.</span>
          </div>
          <div
            style={{
              fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "18px" : "28px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              maxWidth: "100%", // [v18]
              boxSizing: "border-box",
              gap: "50px",
            }}
          >
            {currentPage.type === "syntax" && (
              <div
                className="syntax-sentence"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center", // [v12]
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPage.data.map((element, elementIndex) => {
                  return (
                    <>
                      {element.type === "token" && (
                        <AdvancedTokenElement
                          currentPage={currentPage}
                          handleClickToken={handleClickToken}
                          element={element}
                          elementIndex={elementIndex}
                          lineHeight={lineHeight}
                        />
                      )}
                      {element.type === "group" && (
                        <AdvancedGroupElement
                          currentPage={currentPage}
                          handleClickGroup={handleClickGroup}
                          element={element}
                          elementIndex={elementIndex}
                          lineHeight={lineHeight}
                          isSelectable={!currentPage.isSubmitted}
                        />
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: "30px",
            }}
          >
            <TagButtonList
              tagList={currentPage.tags}
              onClick={handleClickTag}
            />
          </div>
        </div>
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <ArrowButton
          direction="right"
          onClick={() => handleNext()}
          visibleCondition={true}
        />
        )}
        <button
          style={{
            position: "absolute", // 부모(80% width) 안에서 절대 위치
            top: "90px", // 최하단으로부터 10px 떨어짐
            right: "60px", // 우측으로부터 10px 떨어짐
            padding: "10px 35px",
            border: "none",
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "16px",
            fontWeight: 700, // [v11] 문장테스트 동일
            borderRadius: Math.min(window.innerWidth, window.screen.width) <= 768 ? "999px" : "5px", // [가운데 v7] 모바일 알약
            cursor: "pointer",
            backgroundColor: "#FF4444",
            color: "white",
          }}
          onClick={() => deleteData()}
        >
          초기화
        </button>
        <button
          style={{
            // [제출 인라인] 모바일: 둥둥 대신 성분 버튼 아래 흐름 속 배치
            // [v16] 모바일: 화면 기준 fixed — 컬럼 폭에 갇혀 가운데로 밀리던 것 해소
            position: Math.min(window.innerWidth, window.screen.width) <= 768 ? "fixed" : "absolute",
            zIndex: Math.min(window.innerWidth, window.screen.width) <= 768 ? 7 : undefined,
            marginTop: 0,
            bottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "56px" : "90px", // [v16] 하단바(40px) 바로 위
            right: Math.min(window.innerWidth, window.screen.width) <= 768 ? "16px" : "60px",
            padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "8px 22px" : "10px 35px", // [v9] 문장테스트 동일 패딩
            backgroundColor: currentPage.isSubmitted ? "#E6EDEC" : "#0E141B", // [v11] 문장테스트 동일 색
            color: currentPage.isSubmitted === true ? "#737373" : "white",
            border: "none",
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "16px",
            fontWeight: 700, // [v11] 문장테스트 동일
            borderRadius: Math.min(window.innerWidth, window.screen.width) <= 768 ? "999px" : "5px", // [가운데 v7] 모바일 알약
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap", // [v14] "제출하\n기" 줄바꿈 방지
            flexShrink: 0, // [v14] row에서 눌려 좁아지던 것 방지
          }}
          className="syntax-submit"
          onClick={() => handleSubmit()}
        >
          제출하기
        </button>
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={overlayType === 'correct'} />
      </div>
      <div
        className="syntax-bottom"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "40px" : "70px",
          width: "100vw",
          backgroundColor: "#F6F8FA", // 바 색상
          color: "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {(Math.min(window.innerWidth, window.screen.width) <= 768) ? (
          <>
            <div style={{ position: "absolute", left: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="left" onClick={() => handlePrev()} visibleCondition={currentIndex > 0} />
            </div>
            <span>{currentIndex + 1} / {pageList.length}</span>
            <div style={{ position: "absolute", right: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="right" onClick={() => handleNext()} visibleCondition={true} />
            </div>
          </>
        ) : (
          <span>{currentIndex + 1} / {pageList.length}</span>
        )}
      </div>
      <Toast message={toastMessage} visible={toastVisible} />
    </>
  );
};

export default AdvancedPage;
